import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ThemeClasses } from "../types";
import type { Unit } from "../../../../../../store/admin/unitsStore";

export interface TrackingLinkFormData {
  name: string;
  internalNotes?: string;
  units: string[];
  unitSelection: "all" | "specific" | "none";
  accessType: "public" | "protected";
  accessCode?: string;
  activationType: "immediate" | "scheduled" | "inactive";
  activationDate?: string;
  durationValue?: number;
  durationUnit?: "hours" | "days" | "weeks" | "months";
}

interface TrackingLinkFormProps {
  form: UseFormReturn<TrackingLinkFormData>;
  isEdit: boolean;
  loadingSubmit: boolean;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  units: Unit[];
  onSubmit: (data: TrackingLinkFormData) => void;
  onCancel: () => void;
}

export function TrackingLinkForm({
  form,
  isEdit,
  loadingSubmit,
  themeClasses,
  isDarkMode,
  units,
  onSubmit,
  onCancel,
}: TrackingLinkFormProps) {
  const { t } = useTranslation("adminSections");
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isValid },
  } = form;

  const accessType = watch("accessType");
  const activationType = watch("activationType");
  const unitSelection = watch("unitSelection");
  const selectedUnits = watch("units") || [];
  const inputClassName = `w-full text-sm px-4 py-3 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`;

  // Handle unit selection
  const handleUnitChange = (unitId: string, isChecked: boolean) => {
    if (isChecked) {
      form.setValue("units", [...selectedUnits, unitId]);
    } else {
      form.setValue(
        "units",
        selectedUnits.filter((id: string) => id !== unitId),
      );
    }
  };

  // Check if a unit is selected
  const isUnitSelected = (unitId: string) => {
    return selectedUnits.includes(unitId);
  };

  return (
    <form
      className="flex flex-col w-full gap-5"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Nombre del Link */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          Nombre del Link <span className="text-red-500">*</span>
        </label>
        <input
          {...register("name", { required: true })}
          type="text"
          className={`${inputClassName} ${
            errors.name ? "ring-2 ring-red-500" : ""
          }`}
          placeholder="Ej: Ruta Centro Histórico"
        />
        <span className={`text-xs ${themeClasses.textSecondary}`}>
          Identifica este link para tu referencia interna
        </span>
        {errors.name && (
          <span className="text-red-500 text-xs">
            Este campo es obligatorio
          </span>
        )}
      </div>

      {/* Notas internas */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          Notas internas (opcional)
        </label>
        <textarea
          {...register("internalNotes")}
          className={`${inputClassName} resize-none`}
          rows={2}
          placeholder="Información adicional para uso interno..."
        />
      </div>

      {/* Selección de Unidades */}
      <div className="flex flex-col gap-3">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("trackingLinks.unitSelection.title")} <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-col gap-2">
          <label
            className={`flex items-center gap-3 p-4 border ${themeClasses.cardBorder} rounded-lg cursor-pointer transition-all ${
              unitSelection === "none"
                ? "border-primary-500 bg-primary-500/5"
                : themeClasses.cardBg
            }`}
          >
            <input
              type="radio"
              {...register("unitSelection", { required: true })}
              value="none"
              className="w-4 h-4 accent-primary-500"
            />
            <span className={`text-sm ${themeClasses.text}`}>{t("trackingLinks.unitSelection.none")}</span>
          </label>
          <label
            className={`flex items-center gap-3 p-4 border ${themeClasses.cardBorder} rounded-lg cursor-pointer transition-all ${
              unitSelection === "all"
                ? "border-primary-500 bg-primary-500/5"
                : themeClasses.cardBg
            }`}
          >
            <input
              type="radio"
              {...register("unitSelection", { required: true })}
              value="all"
              className="w-4 h-4 accent-primary-500"
            />
            <span className={`text-sm ${themeClasses.text}`}>
              {t("trackingLinks.unitSelection.all")}
            </span>
          </label>
          <label
            className={`flex items-center gap-3 p-4 border ${themeClasses.cardBorder} rounded-lg cursor-pointer transition-all ${
              unitSelection === "specific"
                ? "border-primary-500 bg-primary-500/5"
                : themeClasses.cardBg
            }`}
          >
            <input
              type="radio"
              {...register("unitSelection", { required: true })}
              value="specific"
              className="w-4 h-4 accent-primary-500"
            />
            <span className={`text-sm ${themeClasses.text}`}>
              {t("trackingLinks.unitSelection.specific")}
            </span>
          </label>
        </div>

        {/* Units List - Show when specific is selected */}
        {unitSelection === "specific" && (
          <div
            className={`mt-4 border ${themeClasses.cardBorder} rounded-lg p-4 max-h-60 overflow-y-auto`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className={`text-sm font-medium ${themeClasses.text}`}>
                {t("trackingLinks.unitSelection.selectUnits")}
              </span>
              <span className={`text-xs ${themeClasses.textSecondary}`}>
                {selectedUnits.length} de {units.length} seleccionadas
              </span>
            </div>
            <div className="space-y-2">
              {units.length === 0 ? (
                <div
                  className={`text-center py-4 ${themeClasses.textSecondary}`}
                >
                  {t("trackingLinks.unitSelection.noUnitsAvailable")}
                </div>
              ) : (
                units.map((unit) => (
                  <label
                    key={unit.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                      isUnitSelected(unit.id)
                        ? "bg-primary-500/10 border border-primary-500"
                        : `${themeClasses.cardBg} border ${themeClasses.cardBorder} hover:${themeClasses.buttonHover}`
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isUnitSelected(unit.id)}
                      onChange={(e) =>
                        handleUnitChange(unit.id, e.target.checked)
                      }
                      className="w-4 h-4 accent-primary-500"
                    />
                    <div className="flex-1">
                      <div
                        className={`text-sm font-medium ${themeClasses.text}`}
                      >
                        {unit.name}
                      </div>
                      <div className={`text-xs ${themeClasses.textSecondary}`}>
                        {unit.plate && `Placa: ${unit.plate}`}
                        {unit.plate && unit.type && " • "}
                        {unit.type &&
                          `Tipo: ${unit.type === "car" ? "Automóvil" : "Camión"}`}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      
      {/* Tipo de Acceso */}
      <div className="flex flex-col gap-3">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          Tipo de Acceso <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Público */}
          <label
            className={`flex flex-col items-start gap-2 p-4 border ${themeClasses.cardBorder} rounded-lg cursor-pointer transition-all ${
              accessType === "public"
                ? "border-primary-500 bg-primary-500/5"
                : themeClasses.cardBg
            }`}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <i className="ri-global-line text-xl text-primary-500"></i>
                <span className={`font-medium ${themeClasses.text}`}>
                  {t("trackingLinks.form.accessType.public")}
                </span>
              </div>
              <input
                type="radio"
                {...register("accessType", { required: true })}
                value="public"
                className="w-4 h-4 accent-primary-500"
              />
            </div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("trackingLinks.form.accessType.publicDescription")}
            </span>
          </label>

          {/* Con Código */}
          <label
            className={`flex flex-col items-start gap-2 p-4 border ${themeClasses.cardBorder} rounded-lg cursor-pointer transition-all ${
              accessType === "protected"
                ? "border-primary-500 bg-primary-500/5"
                : themeClasses.cardBg
            }`}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <i className="ri-lock-line text-xl text-amber-500"></i>
                <span className={`font-medium ${themeClasses.text}`}>
                  {t("trackingLinks.form.accessType.protected")}
                </span>
              </div>
              <input
                type="radio"
                {...register("accessType", { required: true })}
                value="protected"
                className="w-4 h-4 accent-primary-500"
              />
            </div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("trackingLinks.form.accessType.protectedDescription")}
            </span>
          </label>
        </div>
      </div>

      {/* Código de Acceso (solo si es protected) */}
      {accessType === "protected" && (
        <div className="flex flex-col gap-2">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("trackingLinks.form.accessCode.label")} <span className="text-red-500">*</span>
          </label>
          <input
            {...register("accessCode", {
              required: accessType === "protected",
              minLength: 4,
              maxLength: 20,
            })}
            type="password"
            className={`${inputClassName} ${
              errors.accessCode ? "ring-2 ring-red-500" : ""
            }`}
            placeholder={
              isEdit && form.getValues("accessCode") === "••••••••"
                ? t("trackingLinks.form.accessCode.placeholderEdit")
                : t("trackingLinks.form.accessCode.placeholder")
            }
          />
          {errors.accessCode && (
            <span className="text-red-500 text-xs">
              {t("trackingLinks.form.accessCode.validation")}
            </span>
          )}
        </div>
      )}

      {/* Vigencia del Link */}
      <div className="flex flex-col gap-3">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("trackingLinks.form.duration.label")} <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-col gap-2">
          <label
            className={`flex items-center gap-3 p-4 border ${themeClasses.cardBorder} rounded-lg cursor-pointer transition-all ${
              activationType === "inactive"
                ? "border-primary-500 bg-primary-500/5"
                : themeClasses.cardBg
            }`}
          >
            <input
              type="radio"
              {...register("activationType", { required: true })}
              value="inactive"
              className="w-4 h-4 accent-primary-500"
            />
            <span className={`text-sm ${themeClasses.text}`}>
              {t("trackingLinks.form.activationType.inactive")}
            </span>
          </label>
          <label
            className={`flex items-center gap-3 p-4 border ${themeClasses.cardBorder} rounded-lg cursor-pointer transition-all ${
              activationType === "immediate"
                ? "border-primary-500 bg-primary-500/5"
                : themeClasses.cardBg
            }`}
          >
            <input
              type="radio"
              {...register("activationType", { required: true })}
              value="immediate"
              className="w-4 h-4 accent-primary-500"
            />
            <span className={`text-sm ${themeClasses.text}`}>
              {t("trackingLinks.form.activationType.immediate")}
            </span>
          </label>
          <label
            className={`flex items-center gap-3 p-4 border ${themeClasses.cardBorder} rounded-lg cursor-pointer transition-all ${
              activationType === "scheduled"
                ? "border-primary-500 bg-primary-500/5"
                : themeClasses.cardBg
            }`}
          >
            <input
              type="radio"
              {...register("activationType", { required: true })}
              value="scheduled"
              className="w-4 h-4 accent-primary-500"
            />
            <span className={`text-sm ${themeClasses.text}`}>
              {t("trackingLinks.form.activationType.scheduled")}
            </span>
          </label>
        </div>
      </div>

      {/* Fecha de Activación (solo si es scheduled) */}
      {activationType === "scheduled" && (
        <div className="flex flex-col gap-2">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("trackingLinks.form.activationDate.label")} <span className="text-red-500">*</span>
          </label>
          <input
            {...register("activationDate", {
              required: activationType === "scheduled",
            })}
            type="datetime-local"
            className={`${inputClassName} ${
              errors.activationDate ? "ring-2 ring-red-500" : ""
            }`}
          />
          {errors.activationDate && (
            <span className="text-red-500 text-xs">
              {t("trackingLinks.form.activationDate.validation")}
            </span>
          )}
        </div>
      )}

      {/* Duración del acceso (solo si no es inactive) */}
      {activationType !== "inactive" && (
        <div className="flex flex-col gap-2">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("trackingLinks.form.duration.label")} <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <input
              {...register("durationValue", {
                required: true,
                min: 1,
                max: 365,
              })}
              type="number"
              min={1}
              max={365}
              className={`${inputClassName} ${
                errors.durationValue ? "ring-2 ring-red-500" : ""
              }`}
              placeholder="24"
            />
            <select
              {...register("durationUnit", { required: true })}
              className={`${inputClassName}`}
            >
              <option value="hours">{t("trackingLinks.form.duration.hours")}</option>
              <option value="days">{t("trackingLinks.form.duration.days")}</option>
              <option value="weeks">{t("trackingLinks.form.duration.weeks")}</option>
              <option value="months">{t("trackingLinks.form.duration.months")}</option>
            </select>
          </div>
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {t("trackingLinks.description.autoDeactivate")}
          </span>
        </div>
      )}

      {/* Información de Seguridad */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-xl text-blue-500 mt-0.5"></i>
          <div>
            <p className="font-medium text-blue-800 text-sm">
              {t("trackingLinks.form.securityInfo.title")}
            </p>
            <p className="text-blue-700 text-xs mt-1">
              {t("trackingLinks.description.publicAccess")}
            </p>
          </div>
        </div>
      </div>

      {/* Botones */}
      <div className="flex gap-4 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className={`flex-1 py-3 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
        >
          {t("trackingLinks.form.cancel")}
        </button>
        <button
          type="submit"
          disabled={!isValid || loadingSubmit}
          className={`flex-1 py-3 px-4 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors ${
            !isValid || loadingSubmit ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loadingSubmit
            ? isEdit
              ? t("trackingLinks.form.saving")
              : t("trackingLinks.form.creating")
            : isEdit
              ? t("trackingLinks.form.save")
              : t("trackingLinks.form.generate")}
        </button>
      </div>
    </form>
  );
}
